import { useState, useEffect } from "react";
import { FaCheck, FaUser, FaTruck, FaBox, FaChevronLeft, FaPhone, FaShoppingBag, FaMapMarkerAlt, FaCreditCard, FaClock } from "react-icons/fa";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import "./OrderTracking.css";
import { Badge, Spinner, ProgressBar } from "react-bootstrap"; 
import { apiFetch, getFullImageUrl, getProductById } from "../api"; 
import { getFallbackImage } from "../utils/imageLoader";

const OrderTracking = () => {

    // State variables
    const navigate = useNavigate();
    const { orderId } = useParams();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [imageLoadErrors, setImageLoadErrors] = useState({});

    // order tracking steps
    const trackingSteps = [
        { name: "Order Placed", status: "pending", icon: FaCheck },
        { name: "Confirmed & Prepared", status: "confirmed", icon: FaUser },
        { name: "Shipped/Out for Delivery", status: "shipped", icon: FaTruck },
        { name: "Delivered", status: "delivered", icon: FaBox },
    ];

    // function for step status
    const getStepStatus = (currentStatus = '', stepStatus) => { 
        const orderSteps = trackingSteps.map(s => s.status);
        const currentLowerStatus = currentStatus.toLowerCase(); 
        const orderIndex = orderSteps.indexOf(currentLowerStatus);
        const stepIndex = orderSteps.indexOf(stepStatus);

        // handle initial pending steps
        if (orderIndex === -1 && stepStatus === 'pending') return "completed"; 
        if (orderIndex >= stepIndex) return "completed";
        if (orderIndex + 1 === stepIndex) return "active";
        return "";
    };

    // progress bar
    const getProgressPercentage = (currentStatus = '') => {
        const orderSteps = trackingSteps.map(s => s.status);
        const currentLowerStatus = currentStatus.toLowerCase();
        const currentIndex = orderSteps.indexOf(currentLowerStatus);
        
        // first step 
        if (currentIndex === -1) return 25; 
        return ((currentIndex + 1) / orderSteps.length) * 100;
    };

    const handleImageError = (itemId) => {
        setImageLoadErrors(prev => ({ ...prev, [itemId]: true }));
    };

    // fetch order details
    useEffect(() => {
        const fetchOrderDetails = async () => {
            if (!orderId) {
                toast.error("No order ID provided.");
                navigate('/orderhistory');
                return;
            }
            setLoading(true);
            setError(null); 
            try {
                const data = await apiFetch(`/api/orders/byId/${orderId}`);
                let fetchedOrder = data.order || data.data || data;

                // image fetching 
                if (Array.isArray(fetchedOrder.orderItems) && fetchedOrder.orderItems.length > 0) {
                    const enrichedItems = await Promise.all(fetchedOrder.orderItems.map(async (it) => {
                        const prod = it.product || null;
                        const itemImage = it.imageUrl || it.image || null;

                        // product already has images or not 
                        const prodHasImages = prod && (Array.isArray(prod.images) ? prod.images.length > 0 : !!prod.image || !!prod.imageUrl);
                        if (prodHasImages || itemImage) return it;

                        // fetch product details
                        const productId = prod?._id || prod;
                        if (!productId) return it;

                        try {
                            const p = await getProductById(productId);
                            const productData = p?.data || p;
                            return { ...it, product: productData || prod };
                        } catch (err) {
                            return it;
                        }
                    }));
                    fetchedOrder = { ...fetchedOrder, orderItems: enrichedItems };
                }

                setOrder(fetchedOrder);
            } catch (err) {
                setError(err.message);
                toast.error(`Tracking Error: ${err.message}`);
                setOrder(null); 
            } finally {
                setLoading(false);
            }
        };
        
        // Delay for better ux
        const timer = setTimeout(fetchOrderDetails, 500);
        return () => clearTimeout(timer);
    }, [orderId, navigate]);

    // loading design
    if (loading) {
        return (
            <div className="container py-5 text-center d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                <div className="text-center">
                    <Spinner animation="border" className="loading-spinner" style={{ color: "#82278A", width: '3rem', height: '3rem' }} />
                    <div className="mt-3">
                        <p className="text-purple mb-2">Loading your order details...</p>
                        <ProgressBar 
                            now={100} 
                            animated 
                            style={{ height: '4px', width: '200px', margin: '0 auto' }}
                            variant="purple"
                        />
                    </div>
                </div>
            </div>
        );
    }

    //  fetch error design
    if (error) {
        return (
            <div className="container py-5 text-center animated-card">
                <div className="alert alert-danger border-0 shadow-sm">
                    <h5 className="alert-heading">Oops! Something went wrong</h5>
                    <p className="mb-3">Error loading order details: {error}</p>
                    <Link to="/orderhistory" className="btn btn-outline-purple mt-2">
                        <FaChevronLeft className="me-2" />
                        Back to Order History
                    </Link>
                </div>
            </div>
        );
    }

    // no order found design
    if (!order) {
        return (
            <div className="container py-5 text-center animated-card">
                <div className="alert alert-warning border-0 shadow-sm">
                    <FaClock className="me-2" />
                    <strong>Order not found.</strong>
                    <p className="mt-2 mb-3">The order you're looking for doesn't exist or may have been removed.</p>
                    <Link to="/orderhistory" className="btn btn-outline-purple">
                        <FaChevronLeft className="me-2" />
                        Back to Order History
                    </Link>
                </div>
            </div>
        );
    }

    // render order tracking details
    const currentStatus = order.status ? order.status.toLowerCase() : 'pending';
    const address = order.address;
    const addressParts = address ? [address.street || address.addressLine1, address.city, address.state, address.pincode] : [];
    const addressDetails = (addressParts.filter(Boolean).join(', ') ) || "Address details unavailable.";
    const totalDue = order.total_amount != null ? order.total_amount.toFixed(2) : 'N/A'; 
    const paymentStatus = order.payment?.status || "Pending"; 
    const orderItems = order.orderItems || [];
    const progressPercentage = getProgressPercentage(currentStatus);

    return (
        <div className="container py-4 page-fade-in">
            {/* Header */}
            <div className="d-flex align-items-center mb-4 animated-card">
                <Link to="/orderhistory" className="btn btn-outline-purple me-3">
                    <FaChevronLeft className="me-1" /> Back to History
                </Link>
                <h4 className="mb-0 text-purple">Order Tracking</h4>
            </div>

            {/* Main Card */}
            <div className="card shadow-lg border-0 overflow-hidden animated-card">
                <div className="card-header bg-purple text-white py-4">
                    <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">
                            <FaShoppingBag className="me-2" />
                            Order #{order._id || 'N/A'} 
                        </h5>
                        <Badge
                        bg={
                            currentStatus === 'cancelled' || currentStatus === 'refunded'
                            ? 'danger'
                            : currentStatus === 'delivered'
                                ? 'success'
                                : 'warning'
                        }
                        className="status-badge fs-6"
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            paddingInline: '14px',  // auto widens box
                            width: 'auto',
                            whiteSpace: 'nowrap',
                            height: 'auto',         // avoid height issues on mobile
                            fontSize: '0.9rem',
                            marginLeft:'33%'
                        }}
                        >
                        {order.status?.toUpperCase() || 'UNKNOWN'}
                        </Badge>

                    </div>
                </div>

                <div className="card-body p-0">
                    <div className="row g-0">
                        {/* Left Column - Progress & Details */}
                        <div className="col-lg-8 p-4">
                            {/* Progress Bar */}
                            <div className="mb-4">
                                <div className="d-flex justify-content-between mb-2">
                                    <span className="text-purple fw-bold">Delivery Progress</span>
                                    <span className="text-muted">{Math.round(progressPercentage)}% Complete</span>
                                </div>
                                <ProgressBar 
                                    now={progressPercentage} 
                                    variant="purple"
                                    animated 
                                    style={{ height: '8px', borderRadius: '4px' }}
                                />
                            </div>

                            {/* Tracking Steps */}
                            <h6 className="text-purple mb-3 fw-bold">
                                <FaTruck className="me-2" />
                                Tracking Steps
                            </h6>
                            <div className="track">
                                {trackingSteps.map((step, index) => (
                                    <div key={index} className={`step ${getStepStatus(currentStatus, step.status)}`}>
                                        <span className="icon">
                                            {getStepStatus(currentStatus, step.status) === "completed" ? 
                                                <FaCheck /> : 
                                                <step.icon />
                                            }
                                        </span>
                                        <span className="text">{step.name}</span>
                                        <span className="date">
                                            {(index === 0 && order.createdAt) ? 
                                                new Date(order.createdAt).toLocaleString() :
                                                (getStepStatus(currentStatus, step.status) === "completed" ? 
                                                    'Completed' : 
                                                    'Pending'
                                                )
                                            }
                                        </span>
                                    </div>
                                ))}
                            </div>

                            <hr className="my-4" />

                            {/* Order Details */}
                            <h6 className="text-purple mb-3 fw-bold">Order Details</h6>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <div className="card border-0 shadow-sm h-100 product-card">
                                        <div className="card-body">
                                            <h6 className="card-title text-purple">
                                                <FaMapMarkerAlt className="me-2" /> 
                                                Shipping Address
                                            </h6>
                                            <p className="card-text mb-0 text-muted">{addressDetails}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <div className="card border-0 shadow-sm h-100 product-card">
                                        <div className="card-body">
                                            <h6 className="card-title text-purple">
                                                <FaCreditCard className="me-2" /> 
                                                Payment Information
                                            </h6>
                                            <p className="card-text mb-1">Total: <strong>₹{totalDue}</strong></p>
                                            <p 
                                                className={`card-text mb-0 badge bg-${
                                                    paymentStatus.toLowerCase() === 'paid' 
                                                    ? 'success' 
                                                    : paymentStatus.toLowerCase() === 'refunded' 
                                                        ? 'warning' 
                                                        : 'danger'
                                                }`} 
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    paddingInline: '12px',   // increases width slightly more than text
                                                    width: 'auto',
                                                    whiteSpace: 'nowrap',
                                                    fontSize: '0.9rem',
                                                    textAlign: 'center',
                                                    marginLeft:'40%'
                                                }}
                                                >
                                                {paymentStatus}
                                                </p>

                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Column - Summary & Help */}
                        <div className="col-lg-4 bg-light p-4">
                            <h6 className="text-purple mb-3 fw-bold">Order Summary</h6>
                            <div className="d-flex justify-content-between mb-2">
                                <span>Subtotal</span>
                                <span>₹{totalDue}</span>
                            </div>
                            <div className="d-flex justify-content-between mb-2">
                                <span>Shipping</span>
                                <span className="text-success">FREE</span>
                            </div>
                            <hr />
                            <div className="d-flex justify-content-between mb-3 fw-bold fs-5">
                                <span>Total</span>
                                <span className="text-purple">₹{totalDue}</span>
                            </div>
                            
                            <hr className="my-4" />
                            
                            <h6 className="text-purple mb-3 fw-bold">Need Help?</h6>
                            <p className="text-muted small mb-3">
                                Having issues with your order? Our support team is here to help.
                            </p>
                            <Link to="/contact" className="btn btn-outline-purple w-100">
                                <FaPhone className="me-2" />
                                Contact Support
                            </Link>
                        </div>
                    </div>

                    {/* Order Items */}
                    <div className="p-4 border-top">
                        <h6 className="text-purple mb-3 fw-bold">
                            Order Items ({orderItems.length})
                        </h6>
                        <div className="row">
                            {orderItems.map((item, itemIndex) => {
                                const itemId = item._id || itemIndex;
                                const prod = item.product || {};
                                const imgCandidate = 
                                    prod?.images?.[0]?.url ||
                                    prod?.images?.[0] ||
                                    prod?.imageUrl ||
                                    prod?.image ||
                                    item?.imageUrl ||
                                    item?.image ||
                                    null;
                                const finalSrc = imageLoadErrors[itemId] ? 
                                    getFallbackImage() : 
                                    getFullImageUrl(imgCandidate) || getFallbackImage();

                                return (
                                    <div key={itemId} className="col-xl-3 col-lg-4 col-md-6 mb-4">
                                        <div className="card h-100 border-0 shadow-sm product-card">
                                            <div className="card-body">
                                                <div className="d-flex">
                                                    <div className="flex-shrink-0">
                                                        <img 
                                                            src={finalSrc} 
                                                            className="img-fluid rounded product-image" 
                                                            alt={prod?.name || "Item"} 
                                                            width="80"
                                                            onError={() => handleImageError(itemId)}
                                                            style={{ transition: 'transform 0.3s ease' }}
                                                        />
                                                    </div>
                                                    <div className="flex-grow-1 ms-3">
                                                        <h6 className="card-title text-dark mb-2">
                                                            {prod?.name || "Product Item"}
                                                        </h6>
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <span className="fw-bold text-purple">
                                                                ₹{item.price != null ? item.price.toFixed(2) : '0.00'}
                                                            </span>
                                                            <span 
                                                            className="badge bg-purple"
                                                            style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                paddingInline: '14px',  // little extra space on both sides
                                                                width: 'auto',
                                                                whiteSpace: 'nowrap',
                                                                fontSize: '0.9rem',
                                                                textAlign: 'center',
                                                                marginLeft:'25%'
                                                            }}
                                                            >
                                                            Qty: {item.quantity || 0}
                                                            </span>

                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrderTracking;